import { useEffect, useRef, useState } from 'react';
import { PackageCheck, Search, Upload, X } from 'lucide-react';
import { Button, Input, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import type { ItemCounts, ItemFilter } from '../model/supply-items.js';
import { ReadyPopover, type ReadyEntry } from './ReadyPopover.js';

export interface DocumentToolbarProps {
  /** Yuborishga tayyor pozitsiyalar — popover shulardan quriladi. */
  entries: readonly ReadyEntry[];
  onRemoveEntry: (index: number) => void;

  counts: ItemCounts;
  filter: ItemFilter;
  onFilterChange: (filter: ItemFilter) => void;

  search: string;
  onSearchChange: (search: string) => void;
  /** Qidiruv va chipdan keyin qolgan satrlar. */
  shown: number;
  total: number;

  selected: number;
  selectable: boolean;
  allSelected: boolean;
  onToggleAll: () => void;
  onClearSelection: () => void;
  onPlaceSelected: () => void;

  onLoad: () => void;
  /** Joylashtirish ruxsati — buxgalterda tanlov ham, yuklash ham yo'q. */
  canPlace: boolean;

  /**
   * Ostidagi jadval bilan BITTA konteynerda turibdimi.
   *
   * Keng ekranda — ha: toolbar konteynerning yuqori qirrasi bo'ladi va
   * pastki burchaklari to'g'ri qoladi. Telefonda ostida kartochkalar
   * turadi, konteyner yo'q — toolbar o'zi yopiq karta bo'ladi.
   */
  attached: boolean;
}

interface ChipProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}

/**
 * FILTR CHIPI — bo'lim sarlavhasining o'rniga.
 *
 * Ilgari jadval ikkita guruhga bo'linardi («JOYLASHTIRILGANLAR» va
 * «QOLGANLAR») va satr joylashtirilgach bir guruhdan ikkinchisiga
 * KO'CHARDI. Ko'chish uchun ro'yxat qayta tuzilardi — hujjatdagi tartib
 * buzilar, aylantirish joyi yo'qolardi.
 *
 * Chip esa hech narsani ko'chirmaydi va saralamaydi: u shunchaki bir
 * qismini vaqtincha berkitadi. Tartib har doim hujjatnikiday qoladi.
 */
const Chip = ({ label, count, active, onClick }: ChipProps) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      'inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-pill)] px-3',
      'text-sm font-medium whitespace-nowrap',
      'transition-[background-color,border-color,color]',
      'duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
      'border',
      active
        ? 'border-transparent bg-primary text-primary-contrast'
        : 'border-border bg-surface text-text-muted hover:bg-surface-2 hover:text-text',
    )}
  >
    {label}
    <span
      className={cn(
        'tabular-nums',
        active ? 'text-primary-contrast/75' : 'text-text-faint',
      )}
    >
      {count}
    </span>
  </button>
);

/**
 * HUJJAT TOOLBARI — jadval bilan BITTA konteynerda.
 *
 * Ilgari bu jadvaldan ajralgan alohida karta edi: ikkita chegara, orada
 * bo'sh joy va «bu panel qaysi jadvalga tegishli?» degan savol. Endi u
 * jadvalning yuqori qirrasi — o'zi yumaloq burchakli va chegarali,
 * jadval esa ostidan chegarasiz davom etadi.
 *
 * Ichida uch guruh, chapdan o'ngga muhimlik bo'yicha:
 *   1. «Tayyor: N» — nima yig'ilgani (popover triggeri);
 *   2. chiplar    — nima ko'rsatilyapti;
 *   3. qidiruv va «Yuklash» — nima qilinadi.
 *
 * «Yuklash» — sahifadagi YAGONA to'ldirilgan tugma. Qatordagi
 * «Joylashtirish» ghost bo'lgani ham shu sababdan: 233 marta
 * takrorlangan to'q ko'k tugma ro'yxatni ko'k devorga aylantirardi va
 * asosiy amal ular orasida yo'qolardi.
 */
export const DocumentToolbar = ({
  entries,
  onRemoveEntry,
  counts,
  filter,
  onFilterChange,
  search,
  onSearchChange,
  shown,
  total,
  selected,
  selectable,
  allSelected,
  onToggleAll,
  onClearSelection,
  onPlaceSelected,
  onLoad,
  canPlace,
  attached,
}: DocumentToolbarProps) => {
  const sentinel = useRef<HTMLDivElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);

  /*
   * Soya FAQAT yopishgandan keyin paydo bo'ladi: tinch turgan sahifada
   * u toolbarni kontentdan ajratib, ortiqcha qatlam yasab ko'rsatardi.
   *
   * Kuzatuv chegarasi HEADER ostidan boshlanadi. `IntersectionObserver`
   * ning ildizi butun ko'rinish oynasi, ya'ni header ORTIDAGI joy ham
   * "ko'rinadigan" hisoblanadi — nishon aynan o'sha yerga kirib ketadi
   * va soya bir header balandligi kechikib chiqardi. O'lchov toolbarning
   * O'ZIDAN olinadi: `getComputedStyle().top` `calc()` va `env()` ni
   * yechib, tayyor piksel qaytaradi.
   */
  useEffect(() => {
    const node = sentinel.current;
    if (node === null || typeof IntersectionObserver !== 'function') return;

    const top = bar.current === null ? 0 : Number.parseFloat(getComputedStyle(bar.current).top);
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(entry !== undefined && !entry.isIntersecting),
      { threshold: 1, rootMargin: `${String(-(Number.isFinite(top) ? top : 0))}px 0px 0px 0px` },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  /*
   * TOOLBAR BALANDLIGI — jadval sarlavhasi shuning ostiga yopishadi.
   *
   * Qat'iy raqam yozib bo'lmaydi: boshqaruvlar tor ekranda ikki qatorga
   * o'raladi va tanlov paydo bo'lganda yana ikkita tugma qo'shiladi.
   * Noto'g'ri o'lchov bilan sarlavha yo toolbar ostiga kirib ketardi,
   * yo orada bo'sh yo'l qolardi.
   *
   * Qiymat hujjat ildiziga yoziladi, chunki uni o'qiydigan qoida
   * (`.doc-table thead th`) uslublar faylida turadi va ekranda bir
   * vaqtda bitta shunday toolbar bo'ladi. React holati emas — o'lcham
   * o'zgarganda sahifa qayta chizilishi shart emas.
   */
  useEffect(() => {
    const node = bar.current;
    if (node === null) return;

    const root = document.documentElement;
    const apply = (): void => {
      root.style.setProperty('--doc-toolbar-h', `${String(node.offsetHeight)}px`);
    };
    apply();

    if (typeof ResizeObserver !== 'function') {
      return () => root.style.removeProperty('--doc-toolbar-h');
    }

    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--doc-toolbar-h');
    };
  }, []);

  /*
   * Chiziq ISHNING ulushi, satrlarning emas: maxraj `all` emas,
   * `all - empty`. Qoldig'i nol satrlar maxrajda qolsa, chiziq hamma
   * ish tugaganda ham to'lmasdi va hujjat abadiy tugamagandek ko'rinardi.
   */
  const work = counts.all - counts.empty;
  const percent = work === 0 ? 0 : Math.round((counts.placed / work) * 100);
  const nothingReady = entries.length === 0;

  return (
    <>
      {/* Kuzatuvchi nishoni — toolbar yopishgan paytni shu aytadi. */}
      <div ref={sentinel} aria-hidden className="h-px" />

      <div
        ref={bar}
        className={cn(
          /*
           * HEADER OSTIGA yopishadi, ekran tepasiga emas. `top-0` bo'lsa
           * toolbar aynan header turgan joyga kelib uni bosib qo'yardi.
           * `--size-topbar` — headerning O'ZI o'qiydigan o'zgaruvchi:
           * balandlik o'zgarsa toolbar ham suriladi.
           */
          /*
           * TOR EKRANDA YOPISHMAYDI. Telefonda chiplar, qidiruv va
           * «Yuklash» bir necha qatorga tushadi va yopishqoq panel
           * ekranning uchdan birini egallab, birinchi kartochkalarni
           * yopib qo'yardi. Jadval sarlavhasi ham faqat keng ekranda bor.
           */
          attached ? 'sticky top-[var(--size-topbar)] z-[var(--z-sticky-bar)]' : 'relative',
          'flex flex-col border border-border bg-surface',
          attached ? 'rounded-t-[var(--radius-lg)]' : 'rounded-2xl',
          'transition-shadow duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
          attached && stuck ? 'shadow-[var(--shadow-1)]' : 'shadow-none',
        )}
      >
        <div className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {canPlace ? (
              <>
                <ReadyPopover entries={entries} onRemove={onRemoveEntry} />
                <span aria-hidden className="hidden h-5 w-px bg-border lg:block" />
              </>
            ) : null}

            <div
              className={cn(
                'flex items-center gap-1.5',
                /* Telefonda chiplar BIR qatorda, yon tomonga suriladi —
                   o'ralib panelni cho'zmasin. Chekkagacha chiqadi, aylantirish
                   chizig'i yashirin. */
                attached
                  ? 'flex-wrap'
                  : cn(
                      '-mx-4 w-[calc(100%+2rem)] overflow-x-auto px-4 pb-0.5',
                      '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                    ),
              )}
            >
              <Chip
                label={t('supply.filterAll')}
                count={counts.all}
                active={filter === 'all'}
                onClick={() => onFilterChange('all')}
              />
              <Chip
                label={t('supply.filterPlaced')}
                count={counts.placed}
                active={filter === 'placed'}
                onClick={() => onFilterChange('placed')}
              />
              <Chip
                label={t('supply.filterPending')}
                count={counts.pending}
                active={filter === 'pending'}
                onClick={() => onFilterChange('pending')}
              />
              {/* Qoldiqsiz satrlar chipi FAQAT ular bo'lganda chiziladi:
                  ko'pchilik hujjatda bunday satr yo'q va doimiy «0» li
                  chip qolgan uchtasini suyultirib yuborardi. */}
              {counts.empty === 0 ? null : (
                <Chip
                  label={t('supply.filterEmpty')}
                  count={counts.empty}
                  active={filter === 'empty'}
                  onClick={() => onFilterChange('empty')}
                />
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/*
              JADVAL qidiruvi — yuqoridagi global qidiruvdan ATAYLAB
              boshqacha: u past (`filter` — 40px), tor va KO'RINADIGAN
              yorliqsiz. O'lchamining o'zi «shu ro'yxat ichida» deb
              aytadi. Yorliq esa `aria-label` da qoladi — ekran o'qigich
              uni baribir eshitadi.
            */}
            <Input
              variant="filter"
              aria-label={t('supply.search')}
              placeholder={t('supply.searchPlaceholder')}
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              prefix={<Search size={15} aria-hidden />}
            />

            {search === '' ? null : (
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('supply.clearSearch')}
                title={t('supply.clearSearch')}
                onClick={() => onSearchChange('')}
                className="px-2"
              >
                <X size={15} aria-hidden />
              </Button>
            )}

            <span className="tabular-nums text-xs whitespace-nowrap text-text-muted">
              {t('supply.showing', { shown, total })}
            </span>

            {canPlace ? (
              <>
                {selectable ? (
                  <Button variant="ghost" size="sm" onClick={onToggleAll}>
                    {t(allSelected ? 'supply.clearSelection' : 'supply.selectAll')}
                  </Button>
                ) : null}

                {selected === 0 ? null : (
                  <>
                    <Button variant="secondary" size="sm" onClick={onPlaceSelected}>
                      <PackageCheck size={15} aria-hidden />
                      {t('supply.placeSelected', { count: selected })}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={onClearSelection}>
                      {t('supply.clearSelection')}
                    </Button>
                  </>
                )}

                {/* Hech narsa tayyor bo'lmasa tugma o'chiq, lekin SABABI
                    bilan: `title` sichqoncha ostida ham, ekran o'qigichda
                    ham tushuntiradi. Telefonda bu yerda YO'Q — u pastda
                    suzib turadi (`FloatingLoadBar`). */}
                {!attached ? null : (
                  <Button
                    variant="accent"
                    disabled={nothingReady}
                    title={nothingReady ? t('supply.loadDisabledHint') : undefined}
                    onClick={onLoad}
                    className={cn('max-lg:w-full', nothingReady ? 'opacity-50' : undefined)}
                  >
                    <Upload size={16} aria-hidden />
                    {t('supply.loadCount', { count: entries.length })}
                  </Button>
                )}
              </>
            ) : null}
          </div>
        </div>

        {/* Bajarilganlik chizig'i — toolbarning PASTKI qirrasida. Raqamni
            o'qimasdan ham ish qancha qolganini ko'rsatadi. */}
        <div
          role="progressbar"
          aria-label={t('supply.progress')}
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-[3px] w-full bg-surface-2"
        >
          <div
            className="h-full bg-success transition-[width] duration-[var(--motion-slow)] ease-[var(--ease-standard)]"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    </>
  );
};
